import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Download, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const AdminReports = () => {
  const navigate = useNavigate();
  const [discipline, setDiscipline] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [attendanceData, setAttendanceData] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    byDiscipline: {}
  });
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    // Set default dates (last 7 days)
    const today = new Date();
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    
    setStartDate(weekAgo.toISOString().split('T')[0]);
    setEndDate(today.toISOString().split('T')[0]);
  }, []);
  
  useEffect(() => {
    if (startDate && endDate) {
      fetchAttendanceData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discipline, startDate, endDate]);
  
  const fetchAttendanceData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (discipline !== 'all') params.append('discipline', discipline);
      if (startDate) params.append('start_date', new Date(startDate).toISOString());
      if (endDate) {
        const endDateTime = new Date(endDate);
        endDateTime.setHours(23, 59, 59, 999);
        params.append('end_date', endDateTime.toISOString());
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/reports/attendance?${params.toString()}`,
        { credentials: 'include' }
      );
      
      const data = await response.json();
      setAttendanceData(data.records);
      
      // Calculate stats
      const byDiscipline = {};
      data.records.forEach(record => {
        byDiscipline[record.discipline] = (byDiscipline[record.discipline] || 0) + 1;
      });
      
      setStats({
        total: data.total,
        byDiscipline
      });
    } catch (error) {
      console.error('Error fetching attendance:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };
  
  const handleExportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (discipline !== 'all') params.append('discipline', discipline);
      if (startDate) params.append('start_date', new Date(startDate).toISOString());
      if (endDate) {
        const endDateTime = new Date(endDate);
        endDateTime.setHours(23, 59, 59, 999);
        params.append('end_date', endDateTime.toISOString());
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/reports/attendance/export?${params.toString()}`,
        { credentials: 'include' }
      );
      
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `asistencia_bka_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Reporte exportado exitosamente');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Error al exportar reporte');
    }
  };
  
  return (
    <div className="min-h-screen bg-black page-transition">
      {/* Header */}
      <div className="bg-zinc-950 border-b border-zinc-800 p-4">
        <div className="max-w-7xl mx-auto flex items-center space-x-4">
          <Button onClick={() => navigate('/dashboard')} variant="ghost" size="sm" data-testid="back-button">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-heading text-white uppercase">REPORTES DE ASISTENCIA</h1>
        </div>
      </div>
      
      {/* Filters */}
      <div className="max-w-7xl mx-auto p-4 space-y-4">
        <Card className="bg-zinc-950 border-zinc-800 p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-white mb-2 block">Disciplina</Label>
              <Select value={discipline} onValueChange={setDiscipline}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white" data-testid="discipline-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="Kickboxing">Kickboxing</SelectItem>
                  <SelectItem value="Boxeo">Boxeo</SelectItem>
                  <SelectItem value="Muay Thai">Muay Thai</SelectItem>
                  <SelectItem value="Jiu Jitsu">Jiu Jitsu</SelectItem>
                  <SelectItem value="Grappling">Grappling</SelectItem>
                  <SelectItem value="MMA">MMA</SelectItem>
                  <SelectItem value="Lucha Libre">Lucha Libre</SelectItem>
                  <SelectItem value="CrossFit">CrossFit</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-white mb-2 block">Fecha Inicio</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-zinc-900 border-zinc-800 text-white"
                data-testid="start-date-input"
              />
            </div>
            
            <div>
              <Label className="text-white mb-2 block">Fecha Fin</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-zinc-900 border-zinc-800 text-white"
                data-testid="end-date-input"
              />
            </div>
            
            <div className="flex items-end">
              <Button
                onClick={handleExportExcel}
                data-testid="export-excel-button"
                className="w-full bg-primary hover:bg-[#b20710] text-white rounded-sm font-bold uppercase tracking-wider shadow-[0_0_15px_rgba(229,9,20,0.3)]"
              >
                <Download className="mr-2 h-4 w-4" />
                EXPORTAR EXCEL
              </Button>
            </div>
          </div>
        </Card>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="bg-zinc-950 border-zinc-800 p-4" data-testid="stat-total">
            <div className="space-y-1">
              <span className="text-xs uppercase tracking-wider text-zinc-400">Total</span>
              <div className="text-3xl font-bold font-heading text-white">{stats.total}</div>
            </div>
          </Card>
          
          {Object.entries(stats.byDiscipline).map(([disc, count]) => (
            <Card key={disc} className="bg-zinc-900/50 border-zinc-800/50 p-4" data-testid={`stat-${disc}`}>
              <div className="space-y-1">
                <span className="text-xs uppercase tracking-wider text-zinc-400">{disc}</span>
                <div className="text-2xl font-bold font-heading text-primary">{count}</div>
              </div>
            </Card>
          ))}
        </div>
      </div>
      
      {/* Attendance Table */}
      <div className="max-w-7xl mx-auto p-4 pb-20">
        <Card className="bg-zinc-950 border-zinc-800 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-heading text-white uppercase">REGISTROS DE ASISTENCIA</h3>
            <span className="text-sm text-zinc-400">{attendanceData.length} registros</span>
          </div>
          
          {loading ? (
            <div className="text-center py-12">
              <p className="text-zinc-500">Cargando...</p>
            </div>
          ) : attendanceData.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="h-12 w-12 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500">No hay registros en el período seleccionado</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="attendance-table">
                <thead>
                  <tr className="border-b border-zinc-800">
                    <th className="text-left py-3 px-4 text-xs font-heading text-zinc-400 uppercase tracking-wider">Fecha/Hora</th>
                    <th className="text-left py-3 px-4 text-xs font-heading text-zinc-400 uppercase tracking-wider">Alumno</th>
                    <th className="text-left py-3 px-4 text-xs font-heading text-zinc-400 uppercase tracking-wider">Disciplina</th>
                    <th className="text-left py-3 px-4 text-xs font-heading text-zinc-400 uppercase tracking-wider">Estado Pago</th>
                  </tr>
                </thead>
                <tbody>
                  {attendanceData.map((record) => {
                    const timestamp = new Date(record.timestamp);
                    const rowKey = record.attendance_id || `${record.student_id}-${record.timestamp}`;
                    let paymentBadgeClass = 'bg-red-500/20 text-red-500';
                    if (record.payment_status === 'valid') paymentBadgeClass = 'bg-green-500/20 text-green-500';
                    else if (record.payment_status === 'expiring') paymentBadgeClass = 'bg-yellow-500/20 text-yellow-500';
                    return (
                      <tr key={rowKey} className="border-b border-zinc-800 hover:bg-zinc-900/50" data-testid={`attendance-row-${rowKey}`}>
                        <td className="py-3 px-4 text-white font-mono text-sm">
                          {timestamp.toLocaleDateString('es-BO')} {timestamp.toLocaleTimeString('es-BO', { hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td className="py-3 px-4 text-white">{record.student_name}</td>
                        <td className="py-3 px-4 text-zinc-400">{record.discipline}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-sm text-xs font-bold uppercase ${paymentBadgeClass}`}>
                            {record.payment_status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AdminReports;
